import React, { useState, useEffect } from 'react';
import { FaTrash, FaPlus, FaUpload, FaUser, FaBuilding, FaListOl, FaMapMarkerAlt, FaUsers } from 'react-icons/fa';

const AdminCommittee = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState({
        name: '',
        position: '',
        role: 'National Committee',
        state: '',
        rank: 0,
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState(null);

    const roles = [
        'Honorable National Patron Members',
        'National Committee',
        'State Committee',
        'Social Justice Wing',
        'Law & Justice Wing',
        'Advisory & Research Committee',
        'Medical & Health Wing',
        'IT Wing',
        'Youth Wing'
    ];

    const [roleFilter, setRoleFilter] = useState('All');

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const response = await fetch("https://crcbharat.com/apis/committee.php");
            const result = await response.json();
            if (result.status === "success") {
                setMembers(result.data);
            }
        } catch (error) {
            console.error("Error fetching committee:", error);
            setMessage({ type: 'error', text: 'Failed to load committee members' });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        setMessage({ type: '', text: '' });

        const data = new FormData();
        data.append('name', formData.name);
        data.append('position', formData.position);
        data.append('role', formData.role);
        data.append('state', formData.state);
        data.append('rank', formData.rank);
        if (formData.image) {
            data.append('image', formData.image);
        }

        try {
            const response = await fetch("https://crcbharat.com/apis/committee.php", {
                method: 'POST',
                body: data
            });
            const result = await response.json();

            if (result.status === "success") {
                setMessage({ type: 'success', text: 'Member added successfully!' });
                setFormData({
                    name: '',
                    position: '',
                    role: 'National Committee',
                    state: '',
                    rank: 0,
                    image: null
                });
                setPreviewUrl(null);
                fetchMembers();
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to add member' });
            }
        } catch (error) {
            console.error("Error adding member:", error);
            setMessage({ type: 'error', text: 'An error occurred while adding the member' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this member? This will also remove their photo from the server.")) return;

        setActionLoading(true);
        try {
            const response = await fetch(`https://crcbharat.com/apis/committee.php?id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();

            if (result.status === "success") {
                setMessage({ type: 'success', text: 'Member deleted successfully' });
                fetchMembers();
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to delete member' });
            }
        } catch (error) {
            console.error("Error deleting member:", error);
            setMessage({ type: 'error', text: 'An error occurred while deleting' });
        } finally {
            setActionLoading(false);
        }
    };

    const filteredMembers = roleFilter === 'All' 
        ? members 
        : members.filter(m => m.role === roleFilter);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-3 md:p-6 max-w-7xl mx-auto space-y-6 min-h-screen bg-gray-50">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-center justify-between bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 gap-4">
                <div className="text-center md:text-left">
                    <h1 className="text-xl md:text-2xl font-bold text-gray-900">Committee Management</h1>
                    <p className="text-sm text-gray-500">Manage national and state committee members</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl hidden md:block">
                    <FaUsers className="text-blue-600 text-2xl" />
                </div>
            </div>

            {message.text && (
                <div className={`p-4 rounded-xl border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form */}
                <div className="lg:col-span-1 order-2 lg:order-1">
                    <form onSubmit={handleSubmit} className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <FaPlus className="text-blue-600" /> Add New Member
                        </h2>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                            <div className="relative">
                                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                                    placeholder="Enter name"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Position / Designation</label>
                            <div className="relative">
                                <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    name="position"
                                    value={formData.position}
                                    onChange={handleInputChange}
                                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                                    placeholder="e.g. National President"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Committee Role</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                            >
                                {roles.map(role => <option key={role} value={role}>{role}</option>)}
                            </select>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">State / Region</label>
                                <div className="relative">
                                    <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        name="state"
                                        required
                                        value={formData.state}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                                        placeholder="Andhra, etc."
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Rank (Order)</label>
                                <div className="relative">
                                    <FaListOl className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="number"
                                        name="rank"
                                        value={formData.rank}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Profile Photo</label>
                            <div className="mt-1 flex flex-col items-center p-3 border-2 border-dashed border-gray-200 rounded-2xl hover:border-blue-400 transition-colors bg-gray-50">
                                {previewUrl ? (
                                    <div className="relative w-20 h-20 mb-2">
                                        <img src={previewUrl} alt="Preview" className="w-full h-full object-cover rounded-full border-2 border-white shadow-md" />
                                        <button 
                                            type="button" 
                                            onClick={() => {setPreviewUrl(null); setFormData(p => ({...p, image: null}))}}
                                            className="absolute -top-1 -right-1 bg-red-500 text-white p-1 rounded-full text-xs"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FaUpload className="text-gray-400 text-xl mb-2" />
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="image-upload"
                                />
                                <label htmlFor="image-upload" className="cursor-pointer text-blue-600 font-medium hover:text-blue-700 text-sm">
                                    {previewUrl ? 'Change Photo' : 'Upload Photo'}
                                </label>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={actionLoading}
                            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                        >
                            {actionLoading ? 'Saving...' : <><FaPlus /> Add Member</>}
                        </button>
                    </form>
                </div>

                {/* List */}
                <div className="lg:col-span-2 space-y-4 order-1 lg:order-2">
                    {/* Role Filters */}
                    <div className="flex overflow-x-auto pb-2 gap-2 scrollbar-none no-scrollbar">
                        <button
                            onClick={() => setRoleFilter('All')}
                            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${roleFilter === 'All' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
                        >
                            All
                        </button>
                        {roles.map(role => (
                            <button
                                key={role}
                                onClick={() => setRoleFilter(role)}
                                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${roleFilter === role ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
                            >
                                {role}
                            </button>
                        ))}
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-4 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
                            <h2 className="font-semibold text-gray-800 text-sm md:text-base">
                                {roleFilter} ({filteredMembers.length})
                            </h2>
                        </div>
                        
                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredMembers.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-10 text-center text-gray-400">No members found</td>
                                        </tr>
                                    ) : (
                                        filteredMembers.map((member) => (
                                            <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <img 
                                                            src={member.image_url || 'https://via.placeholder.com/40'} 
                                                            alt={member.name} 
                                                            className="w-10 h-10 rounded-full object-cover border border-gray-100" 
                                                        />
                                                        <div>
                                                            <div className="font-bold text-gray-900 text-sm">{member.name}</div>
                                                            <div className="text-[10px] text-blue-600 font-medium">{member.position}</div>
                                                            <div className="text-[10px] text-gray-400 flex items-center gap-1">
                                                                <FaMapMarkerAlt className="text-[8px]" /> {member.state} | Rank: {member.rank}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-md">{member.role}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button
                                                        onClick={() => handleDelete(member.id)}
                                                        disabled={actionLoading}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile List View */}
                        <div className="md:hidden divide-y divide-gray-50">
                            {filteredMembers.length === 0 ? (
                                <div className="p-10 text-center text-gray-400">No members found</div>
                            ) : (
                                filteredMembers.map((member) => (
                                    <div key={member.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                                        <div className="flex items-center gap-3">
                                            <img 
                                                src={member.image_url || 'https://via.placeholder.com/40'} 
                                                alt={member.name} 
                                                className="w-12 h-12 rounded-full object-cover border border-gray-100 shadow-sm" 
                                            />
                                            <div className="flex flex-col">
                                                <span className="font-bold text-gray-900 text-sm leading-tight">{member.name}</span>
                                                <span className="text-[11px] text-blue-600 font-medium mb-0.5">{member.position}</span>
                                                <span className="text-[10px] text-gray-500 bg-gray-100 self-start px-1.5 py-0.5 rounded uppercase font-semibold">{member.role}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(member.id)}
                                            disabled={actionLoading}
                                            className="p-3 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                        >
                                            <FaTrash size={16} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCommittee;
